import * as React from 'react';
import { 
  Box, 
  FormGroup,
  FormControl, 
  Select, 
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  NativeSelect,
  InputLabel
} from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import BarGroupChart from './Charts/BarGroupChart';
import LineGroupChart from './Charts/LineGroupChart';
import PieGroupChart from './Charts/PieGroupChart';

import ColorModal from '../modals/ColorModal'

const CHART_WIDTH = 840;
const chartOptions = ['Bar', 'Line', 'Pie'];

const Visualization = (props) => {
  const {data} = props;
  const allGroups = ["None" ,...data.map((group) => group.group)];

  const [chart, setChart] = React.useState("Bar");
  const [contextMenu, setContextMenu] = React.useState(null);

  const chartRef = React.useRef();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const [openColor, setOpenColor] = React.useState(false);
  const chartColor = JSON.parse(sessionStorage.getItem('chartColor')) || ['#7B53EF'];

  const [sortMode, setSortMode] = React.useState("None");
  const [sortOrder, setSortOrder] = React.useState("ASC");
  const [colorMode, setColorMode] = React.useState(true);

  const [chartData, setChartData] = React.useState(data);

  React.useEffect(() => {
    const group = data.find(item => item.group === sortMode)
    if(!group) {
      setChartData(data);
    }
    else {
      const {values} = group;
      let sortedIndices = [];
      if(sortOrder === "ASC")
        sortedIndices = values.map((_, index) => index).sort((a, b) => values[a].value - values[b].value).map(index => index);
      else
        sortedIndices = values.map((_, index) => index).sort((a, b) => values[b].value - values[a].value).map(index => index);
      const newChartData = data.map(item => {
        const {values: content} = item;
        const newContent = sortedIndices.map(index => content[index]);
        return {
          ...item,
          values: newContent
        }
      })
      setChartData(newChartData)
    }
  }, [sortMode, sortOrder, data]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: e.clientX + 2,
            mouseY: e.clientY - 6,
          }
        : null,
    );
  }
  
  const handleClose = () => {
    setContextMenu(null);
  };

  const handleChart = (e) => {
    setChart(e.target.value)
  }

  const handleDownloadImage = (e) => {
    if(chartRef.current) {
      chartRef.current.saveAsImage();
    }
  }

  const handleSettingClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingClose = () => {
    setAnchorEl(null);
  };

  const handleCloseColor = () => {
    setOpenColor(false);
  }

  const handleEditColor = () => {
    setOpenColor(true);
    setAnchorEl(null);
  }

  const handleSaveColor = (newColor) => {
    sessionStorage.setItem('chartColor', JSON.stringify(newColor));
    setOpenColor(false);
  }

  return (
    <Box onContextMenu={(e) => handleContextMenu(e)}>
      <Box className="flex align-center">
        <FormControl sx={{flex: 1}} variant='standard' fullWidth>
          {/* <InputLabel id="demo-simple-select-label">Chart</InputLabel> */}
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={chart}
            margin='dense'
            label="Chart"
            onChange={handleChart}
            fullWidth={true}
            >
              {
                chartOptions.map(item=> (<MenuItem key={item} value={item}>{item}</MenuItem>))
              }
          </Select>
        </FormControl>
        <Tooltip title="Chart Setting">
          <IconButton
            onClick={handleSettingClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box className='w-full' sx={{mt: 1}}>
        {chart === 'Bar' && <BarGroupChart data={chartData} color={chartColor} width={CHART_WIDTH} ref={chartRef} colorMode={colorMode} />}
        {chart === 'Line' && <LineGroupChart data={chartData} color={chartColor} width={CHART_WIDTH} ref={chartRef} />}
        {chart === 'Pie' && <PieGroupChart data={chartData} color={chartColor} width={CHART_WIDTH} ref={chartRef} />}
      </Box>
      <Box className='w-full flex justify-center align-center' sx={{mt: 1, gap: 1}}>
        <InputLabel variant="standard" htmlFor="uncontrolled-native">
          Sort By:
        </InputLabel>
        <NativeSelect 
          id="sort-by-selector"
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
        >
          {allGroups.map(group => <option key={group} value={group}>{group}</option>)}
        </NativeSelect> 
        <InputLabel variant="standard" htmlFor="uncontrolled-native">
          Sort Order:
        </InputLabel>
        <NativeSelect 
          id="sort-order-selector"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="ASC">Ascending</option>
          <option value="DES">Descending</option>
        </NativeSelect>
        <FormGroup>
          <FormControlLabel control={<Checkbox checked={colorMode} onChange={(e) => setColorMode(e.target.checked)} />} labelPlacement="start" label="Apply Same Color" />
        </FormGroup>
      </Box>
      <ColorModal open={openColor} value={chartColor} handleClose={handleCloseColor} handleSave={handleSaveColor} />
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleDownloadImage}>Download As Png</MenuItem>
      </Menu>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleSettingClose}
        onClick={handleSettingClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '& .MuiList-root': {
              py: '4px'
            },
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
          <MenuItem sx={{fontSize: '0.875rem'}} onClick={handleEditColor}>
            Edit Color
          </MenuItem>
      </Menu>
    </Box>
  )
};

export default Visualization;
